import {
  EMPTY_CHART_FILTERS,
  type ChartFilterState,
} from '@/pages/dashboard/dashboard-charts'
import type { SortingState } from '@tanstack/react-table'

/** Prefix for table filter/sort/pagination blobs cleared on logout. */
export const TABLE_UI_SESSION_PREFIX = 'cdTableUi:v1:'

export function tableUiSessionStorageKey(sliceId: string): string {
  const safe = sliceId.replace(/[^a-zA-Z0-9._-]/g, '')
  return `${TABLE_UI_SESSION_PREFIX}${safe || 'default'}`
}

export function clearTableUiSessionStorage(): void {
  if (typeof window === 'undefined') return
  try {
    const toRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith(TABLE_UI_SESSION_PREFIX)) toRemove.push(k)
    }
    for (const k of toRemove) {
      localStorage.removeItem(k)
    }
  } catch {
    /* ignore */
  }
}

function parseJsonRecord(raw: string | null): Record<string, unknown> | null {
  if (raw == null) return null
  try {
    const v: unknown = JSON.parse(raw)
    return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null
  } catch {
    return null
  }
}

function writeSlice(sliceId: string, data: Record<string, unknown>): void {
  try {
    localStorage.setItem(tableUiSessionStorageKey(sliceId), JSON.stringify(data))
  } catch {
    /* ignore */
  }
}

// --- Dashboard excursions ----------------------------------------------------

const SLICE_DASHBOARD_EXCURSIONS = 'dashboard-excursions'

const EXCURSIONS_SORT_FIELDS = new Set(['id', 'severity', 'status', 'date'])
const EXCURSIONS_PAGE_SIZES = new Set([10, 20, 50, 100])

function parseStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null
  const items = value.filter((item): item is string => typeof item === 'string' && item.length > 0)
  return items
}

function parseChartFilters(value: unknown): ChartFilterState {
  if (value === null) return EMPTY_CHART_FILTERS
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return EMPTY_CHART_FILTERS
  }
  const o = value as Record<string, unknown>
  const type = o.type
  const v = o.value
  if (type === 'severity' && typeof v === 'string') {
    return { severities: [v], products: [] }
  }
  if (type === 'product' && typeof v === 'string') {
    return { severities: [], products: [v] }
  }
  const severities = parseStringArray(o.severities)
  const products = parseStringArray(o.products)
  if (severities !== null || products !== null) {
    return {
      severities: severities ?? [],
      products: products ?? [],
    }
  }
  return EMPTY_CHART_FILTERS
}

export type DashboardExcursionsTableUiPersisted = {
  searchQuery: string
  sortField: 'id' | 'severity' | 'status' | 'date' | null
  sortDirection: 'asc' | 'desc' | null
  pageIndex: number
  pageSize: 10 | 20 | 50 | 100
  chartFilters: ChartFilterState
}

const DEFAULT_DASHBOARD_EXCURSIONS: DashboardExcursionsTableUiPersisted = {
  searchQuery: '',
  sortField: 'severity',
  sortDirection: 'desc',
  pageIndex: 0,
  pageSize: 10,
  chartFilters: EMPTY_CHART_FILTERS,
}

export function readDashboardExcursionsTableUi(): DashboardExcursionsTableUiPersisted {
  if (typeof window === 'undefined') return DEFAULT_DASHBOARD_EXCURSIONS
  const rec = parseJsonRecord(localStorage.getItem(tableUiSessionStorageKey(SLICE_DASHBOARD_EXCURSIONS)))
  if (!rec || rec.v !== 1) return DEFAULT_DASHBOARD_EXCURSIONS

  const searchQuery = typeof rec.searchQuery === 'string' ? rec.searchQuery : DEFAULT_DASHBOARD_EXCURSIONS.searchQuery
  const sortFieldRaw = rec.sortField
  const sortField =
    typeof sortFieldRaw === 'string' && EXCURSIONS_SORT_FIELDS.has(sortFieldRaw)
      ? (sortFieldRaw as DashboardExcursionsTableUiPersisted['sortField'])
      : DEFAULT_DASHBOARD_EXCURSIONS.sortField
  const sd = rec.sortDirection
  const sortDirection =
    sd === 'asc' || sd === 'desc' || sd === null ? sd : DEFAULT_DASHBOARD_EXCURSIONS.sortDirection
  const pi = rec.pageIndex
  const pageIndex =
    typeof pi === 'number' && Number.isInteger(pi) && pi >= 0 && pi < 1_000_000
      ? pi
      : DEFAULT_DASHBOARD_EXCURSIONS.pageIndex
  const ps = rec.pageSize
  const pageSize =
    typeof ps === 'number' && EXCURSIONS_PAGE_SIZES.has(ps as 10 | 20 | 50 | 100)
      ? (ps as 10 | 20 | 50 | 100)
      : DEFAULT_DASHBOARD_EXCURSIONS.pageSize
  const chartFilters =
    rec.chartFilters !== undefined
      ? parseChartFilters(rec.chartFilters)
      : parseChartFilters(rec.chartFilter)

  return { searchQuery, sortField, sortDirection, pageIndex, pageSize, chartFilters }
}

export function writeDashboardExcursionsTableUi(state: DashboardExcursionsTableUiPersisted): void {
  writeSlice(SLICE_DASHBOARD_EXCURSIONS, {
    v: 1,
    searchQuery: state.searchQuery,
    sortField: state.sortField,
    sortDirection: state.sortDirection,
    pageIndex: state.pageIndex,
    pageSize: state.pageSize,
    chartFilters: state.chartFilters,
  })
}

// --- Workflow runs list -------------------------------------------------------

const SLICE_WORKFLOW_RUNS = 'workflow-runs'

const RUNS_SORT_IDS = new Set(['run_id', 'workflow_type', 'status', 'started_at', 'trigger'])
const RUNS_PAGE_SIZES = new Set([10, 20, 50, 100])

export type WorkflowRunsTableUiPersisted = {
  workflowType: string
  pageIndex: number
  pageSize: number
  sorting: SortingState
}

const DEFAULT_RUNS_SORTING: SortingState = [{ id: 'started_at', desc: true }]

function parseSortingState(value: unknown): SortingState {
  if (!Array.isArray(value) || value.length === 0) return DEFAULT_RUNS_SORTING
  const first = value[0]
  if (typeof first !== 'object' || first === null) return DEFAULT_RUNS_SORTING
  const o = first as Record<string, unknown>
  const id = o.id
  const desc = o.desc
  if (typeof id !== 'string' || !RUNS_SORT_IDS.has(id)) return DEFAULT_RUNS_SORTING
  if (typeof desc !== 'boolean') return DEFAULT_RUNS_SORTING
  return [{ id, desc }]
}

export function readWorkflowRunsTableUi(): WorkflowRunsTableUiPersisted {
  const defaults: WorkflowRunsTableUiPersisted = {
    workflowType: 'All',
    pageIndex: 0,
    pageSize: 20,
    sorting: DEFAULT_RUNS_SORTING,
  }
  if (typeof window === 'undefined') return defaults
  const rec = parseJsonRecord(localStorage.getItem(tableUiSessionStorageKey(SLICE_WORKFLOW_RUNS)))
  if (!rec || rec.v !== 1) return defaults

  const wt = rec.workflowType
  const workflowType =
    typeof wt === 'string' && wt.trim().length > 0 ? wt.trim() : defaults.workflowType

  const pi = rec.pageIndex
  const pageIndex =
    typeof pi === 'number' && Number.isInteger(pi) && pi >= 0 && pi < 1_000_000 ? pi : defaults.pageIndex
  const ps = rec.pageSize
  const pageSize =
    typeof ps === 'number' && Number.isInteger(ps) && RUNS_PAGE_SIZES.has(ps) ? ps : defaults.pageSize
  const sorting = parseSortingState(rec.sorting)

  return { workflowType, pageIndex, pageSize, sorting }
}

export function writeWorkflowRunsTableUi(state: WorkflowRunsTableUiPersisted): void {
  writeSlice(SLICE_WORKFLOW_RUNS, {
    v: 1,
    workflowType: state.workflowType,
    pageIndex: state.pageIndex,
    pageSize: state.pageSize,
    sorting: state.sorting,
  })
}

// --- Data catalog -------------------------------------------------------------

const SLICE_DATA_CATALOG = 'data-catalog'

const DATA_CATALOG_SORT = new Set(['product', 'node', 'businessUnit', 'wat', 'waferSort', 'finalTest'])
const DATA_CATALOG_SOURCE_IDS = new Set(['ws-optimal-plus', 'ft-optimal-plus'])

export type DataCatalogTableUiPersisted = {
  searchQuery: string
  sortField: 'product' | 'node' | 'businessUnit' | 'wat' | 'waferSort' | 'finalTest' | null
  sortDirection: 'asc' | 'desc' | null
  selectedSourceId: string
}

const DEFAULT_DATA_CATALOG: DataCatalogTableUiPersisted = {
  searchQuery: '',
  sortField: 'product',
  sortDirection: 'asc',
  selectedSourceId: 'ws-optimal-plus',
}

export function readDataCatalogTableUi(): DataCatalogTableUiPersisted {
  if (typeof window === 'undefined') return DEFAULT_DATA_CATALOG
  const rec = parseJsonRecord(localStorage.getItem(tableUiSessionStorageKey(SLICE_DATA_CATALOG)))
  if (!rec || rec.v !== 1) return DEFAULT_DATA_CATALOG

  const searchQuery = typeof rec.searchQuery === 'string' ? rec.searchQuery : DEFAULT_DATA_CATALOG.searchQuery
  const sf = rec.sortField
  const sortField =
    typeof sf === 'string' && DATA_CATALOG_SORT.has(sf)
      ? (sf as DataCatalogTableUiPersisted['sortField'])
      : DEFAULT_DATA_CATALOG.sortField
  const sd = rec.sortDirection
  const sortDirection =
    sd === 'asc' || sd === 'desc' || sd === null ? sd : DEFAULT_DATA_CATALOG.sortDirection
  const sid = rec.selectedSourceId
  const selectedSourceId =
    typeof sid === 'string' && DATA_CATALOG_SOURCE_IDS.has(sid) ? sid : DEFAULT_DATA_CATALOG.selectedSourceId

  return { searchQuery, sortField, sortDirection, selectedSourceId }
}

export function writeDataCatalogTableUi(state: DataCatalogTableUiPersisted): void {
  writeSlice(SLICE_DATA_CATALOG, {
    v: 1,
    searchQuery: state.searchQuery,
    sortField: state.sortField,
    sortDirection: state.sortDirection,
    selectedSourceId: state.selectedSourceId,
  })
}

// --- Workflows catalog (client-side search) ---------------------------------

const SLICE_WORKFLOWS_CATALOG = 'workflows-catalog'

export function readWorkflowsCatalogTableSearch(): string {
  if (typeof window === 'undefined') return ''
  const rec = parseJsonRecord(localStorage.getItem(tableUiSessionStorageKey(SLICE_WORKFLOWS_CATALOG)))
  if (!rec || rec.v !== 1) return ''
  return typeof rec.searchQuery === 'string' ? rec.searchQuery : ''
}

export function writeWorkflowsCatalogTableSearch(searchQuery: string): void {
  writeSlice(SLICE_WORKFLOWS_CATALOG, { v: 1, searchQuery })
}

// --- Knowledge documents ------------------------------------------------------

const SLICE_KNOWLEDGE_DOCUMENTS = 'knowledge-documents'

const KNOWLEDGE_SORT = new Set(['name', 'dateUploaded', 'type'])

export type KnowledgeDocumentsTableUiPersisted = {
  searchQuery: string
  sortField: 'name' | 'dateUploaded' | 'type' | null
  sortDirection: 'asc' | 'desc' | null
}

const DEFAULT_KNOWLEDGE_DOCS: KnowledgeDocumentsTableUiPersisted = {
  searchQuery: '',
  sortField: 'dateUploaded',
  sortDirection: 'desc',
}

export function readKnowledgeDocumentsTableUi(): KnowledgeDocumentsTableUiPersisted {
  if (typeof window === 'undefined') return DEFAULT_KNOWLEDGE_DOCS
  const rec = parseJsonRecord(localStorage.getItem(tableUiSessionStorageKey(SLICE_KNOWLEDGE_DOCUMENTS)))
  if (!rec || rec.v !== 1) return DEFAULT_KNOWLEDGE_DOCS

  const searchQuery = typeof rec.searchQuery === 'string' ? rec.searchQuery : DEFAULT_KNOWLEDGE_DOCS.searchQuery
  const sf = rec.sortField
  const sortField =
    typeof sf === 'string' && KNOWLEDGE_SORT.has(sf)
      ? (sf as KnowledgeDocumentsTableUiPersisted['sortField'])
      : DEFAULT_KNOWLEDGE_DOCS.sortField
  const sd = rec.sortDirection
  const sortDirection =
    sd === 'asc' || sd === 'desc' || sd === null ? sd : DEFAULT_KNOWLEDGE_DOCS.sortDirection

  return { searchQuery, sortField, sortDirection }
}

export function writeKnowledgeDocumentsTableUi(state: KnowledgeDocumentsTableUiPersisted): void {
  writeSlice(SLICE_KNOWLEDGE_DOCUMENTS, {
    v: 1,
    searchQuery: state.searchQuery,
    sortField: state.sortField,
    sortDirection: state.sortDirection,
  })
}
